import { useCallback, useRef } from 'react';
import { useVoiceInput, type VoiceInputStatus } from './useVoiceInput';
import { joinTranscript } from '../lib/voice/join-transcript';

export interface UseResumeDictationArgs {
  /**
   * App locale. Accepted for callers' sake but deliberately not passed on: the
   * UI language says nothing about the language spoken, and the transcription model detects it
   * (including English/Arabic mixed in one sentence).
   */
  locale?: string;
  /**
   * Called once per session with the full field text — the base captured
   * when `start()` was called plus the transcript — and `isFinal: true`.
   */
  onTranscript: (fullText: string, isFinal: boolean) => void;
  /**
   * When this flips to `false` mid-session (e.g. the composer starts parsing and
   * swaps its toolbar out), an active recording is discarded so the mic can't
   * keep running with no stop control on screen. Defaults to `true`.
   */
  enabled?: boolean;
}

export interface ResumeDictationController {
  /** True while recording or transcribing: the field is locked and the stop button shows. */
  isListening: boolean;
  status: VoiceInputStatus;
  /** 0..1 "is it hearing me" signal while listening. */
  level: number;
  /** Whether this platform can record at all. */
  isSupported: boolean;
  /** Begin recording; the transcript is appended onto `baseText` once it lands. */
  start: (baseText?: string) => Promise<void>;
  stop: () => void;
}

// Tap-to-talk for the resume composer, on the same record → transcribe path as
// every other text field (`useVoiceInput`). Works in both
// `ResumeImportComposer.tsx` (native) and its `.web.tsx` sibling.
export function useResumeDictation({
  onTranscript,
  enabled = true,
}: UseResumeDictationArgs): ResumeDictationController {
  const baseRef = useRef('');
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const voice = useVoiceInput({
    role: 'seeker',
    enabled,
    onTranscript: (transcript) => {
      onTranscriptRef.current(joinTranscript(baseRef.current, transcript), true);
    },
  });

  const start = useCallback(
    async (baseText = '') => {
      baseRef.current = baseText;
      await voice.start();
    },
    [voice],
  );

  const stop = useCallback(() => {
    void voice.stop();
  }, [voice]);

  return {
    isListening: voice.status !== 'idle',
    status: voice.status,
    level: voice.level,
    isSupported: true,
    start,
    stop,
  };
}
